import * as React from "react";
import {
  TIMELINE_MAX_YEAR,
  TIMELINE_MIN_YEAR,
  TimelineBucket,
  bucketIndexForYear,
  clampYear,
  formatBucketLabel,
  formatYear,
  formatYearShort,
  isFullRange,
  timelineBucketRanges,
} from "../../backend/lib/timeline";

export interface TimelineRange {
  from: number;
  to: number;
}

interface TimelineScrubberProps {
  /** Counts per bucket, in `timelineBucketRanges()` order. */
  buckets: TimelineBucket[];
  /**
   * Objects carrying a usable date, for the current non-date filters. A head
   * count — never the sum of `buckets`, which counts a multi-period object
   * once per period it spans.
   */
  total: number;
  /** The committed range. */
  range: TimelineRange;
  /** Fired on release — not during a drag, so a drag costs one search. */
  onRangeCommit: (range: TimelineRange) => void;
  /** True while a histogram request is in flight. */
  loading?: boolean;
}

/** Presets come before the custom range: nobody drags to find "the 20th century". */
const PRESETS: Array<{ label: string; from: number; to: number }> = [
  { label: "ALL", from: TIMELINE_MIN_YEAR, to: TIMELINE_MAX_YEAR },
  { label: "PRE-1500", from: TIMELINE_MIN_YEAR, to: 1499 },
  { label: "1500–1800", from: 1500, to: 1799 },
  { label: "19TH C.", from: 1800, to: 1899 },
  { label: "20TH C.", from: 1900, to: 1999 },
  { label: "2000+", from: 2000, to: TIMELINE_MAX_YEAR },
];

const CHART_HEIGHT = 92;
const AXIS_BAND = 18;
/** Gap between adjacent bars, so fills are separated by surface, not by a border. */
const BAR_GAP = 2;
/**
 * Smallest drawn bar for a non-empty bucket. The corpus spans 16 objects in
 * 4000–3001 BCE against 9,505 in 1975–1999; at true scale the sparse buckets
 * round to a fifth of a pixel and vanish. The floor keeps them visible without
 * touching the scale everywhere else — the exact counts are on hover and in the
 * table view below.
 */
const MIN_BAR_HEIGHT = 3;

/** A bar with rounded top corners, square where it meets the baseline. */
function barPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  return [
    `M ${x} ${y + h}`,
    `L ${x} ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `L ${x + w - r} ${y}`,
    `Q ${x + w} ${y} ${x + w} ${y + r}`,
    `L ${x + w} ${y + h}`,
    "Z",
  ].join(" ");
}

type DragMode = "from" | "to" | "new" | null;

export const TimelineScrubber: React.FC<TimelineScrubberProps> = ({
  buckets,
  total,
  range,
  onRangeCommit,
  loading = false,
}) => {
  const ranges = React.useMemo(() => timelineBucketRanges(), []);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [width, setWidth] = React.useState(720);
  const [hovered, setHovered] = React.useState<number | null>(null);
  const [showTable, setShowTable] = React.useState(false);

  /**
   * The range being dragged, before release. Kept separate from `range` so the
   * committed value only changes once, on pointer-up — a drag across the axis
   * would otherwise fire twenty searches.
   */
  const [draft, setDraft] = React.useState<TimelineRange | null>(null);
  const dragMode = React.useRef<DragMode>(null);
  const dragAnchor = React.useRef<number>(0);

  const active = draft ?? range;

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const counts = React.useMemo(
    () => ranges.map((_, i) => buckets[i]?.count ?? 0),
    [buckets, ranges],
  );
  const maxCount = React.useMemo(
    () => Math.max(1, ...counts),
    [counts],
  );

  const columnWidth = width / ranges.length;
  const fromIndex = bucketIndexForYear(active.from);
  const toIndex = bucketIndexForYear(active.to);
  const isAll = isFullRange(active.from, active.to);

  /** Which bucket a clientX falls on. */
  const bucketAt = React.useCallback(
    (clientX: number): number => {
      const box = svgRef.current?.getBoundingClientRect();
      if (!box) return 0;
      const ratio = (clientX - box.left) / box.width;
      return Math.min(
        ranges.length - 1,
        Math.max(0, Math.floor(ratio * ranges.length)),
      );
    },
    [ranges.length],
  );

  const rangeFromIndices = React.useCallback(
    (a: number, b: number): TimelineRange => {
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      return { from: ranges[lo].start, to: ranges[hi].end };
    },
    [ranges],
  );

  const handlePointerDown = (event: React.PointerEvent) => {
    // Right-click and middle-click should not start a selection.
    if (event.button !== 0) return;
    const index = bucketAt(event.clientX);
    // Grabbing near an existing handle adjusts that end; anywhere else starts a
    // fresh selection from the bucket under the pointer.
    const nearFrom = Math.abs(index - fromIndex);
    const nearTo = Math.abs(index - toIndex);
    if (!isAll && Math.min(nearFrom, nearTo) <= 1) {
      dragMode.current = nearFrom <= nearTo ? "from" : "to";
      dragAnchor.current = nearFrom <= nearTo ? toIndex : fromIndex;
    } else {
      dragMode.current = "new";
      dragAnchor.current = index;
    }
    setDraft(rangeFromIndices(dragAnchor.current, index));
    // Capture on the svg rather than the hit rect under the pointer: the drag
    // crosses columns, and a capture held by a child would end at its edge.
    svgRef.current?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const index = bucketAt(event.clientX);
    if (!dragMode.current) {
      setHovered(index);
      return;
    }
    setHovered(index);
    setDraft(rangeFromIndices(dragAnchor.current, index));
  };

  const endDrag = (event: React.PointerEvent) => {
    if (!dragMode.current) return;
    dragMode.current = null;
    if (svgRef.current?.hasPointerCapture?.(event.pointerId)) {
      svgRef.current.releasePointerCapture(event.pointerId);
    }
    if (draft) onRangeCommit(draft);
    setDraft(null);
  };

  /** Arrow keys nudge a handle by one bucket; Home/End jump to the axis ends. */
  const handleKeyDown = (which: "from" | "to") => (
    event: React.KeyboardEvent,
  ) => {
    const index = which === "from" ? fromIndex : toIndex;
    let next = index;
    if (event.key === "ArrowLeft") next = index - 1;
    else if (event.key === "ArrowRight") next = index + 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = ranges.length - 1;
    else return;
    event.preventDefault();
    next = Math.min(ranges.length - 1, Math.max(0, next));
    const other = which === "from" ? toIndex : fromIndex;
    onRangeCommit(rangeFromIndices(other, next));
  };

  const commitYear = (which: "from" | "to", raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const year = clampYear(parsed);
    const next =
      which === "from"
        ? { from: year, to: Math.max(year, range.to) }
        : { from: Math.min(year, range.from), to: year };
    onRangeCommit(next);
  };

  const hoveredBucket = hovered !== null ? ranges[hovered] : null;

  // At narrow widths most tick labels would collide, so only every Nth is
  // drawn — enough to anchor the axis without overlapping text.
  const tickStep = Math.max(1, Math.ceil(90 / Math.max(columnWidth, 1)));

  return (
    <div className="timeline-scrubber pt-1" ref={containerRef}>
      {/* Readout: the resolved years, always in text, never only in the chart */}
      <div className="flex flex-wrap items-baseline justify-between gap-2 pb-1">
        <p className="text-sm opacity-75">
          TIME PERIOD:{" "}
          <span className="font-bold" style={{ color: "var(--accent-color)" }}>
            {isAll
              ? "ALL"
              : `${formatYear(active.from)} – ${formatYear(active.to)}`}
          </span>
        </p>
        <p className="text-xs opacity-50">
          {/* The head count, not a sum of the bars: an object dated across
              several periods is in each bar, so adding them overcounts. */}
          {total.toLocaleString()} dated{" "}
          {total === 1 ? "artifact" : "artifacts"} on the timeline
        </p>
      </div>

      {/* Presets first — the range most readers want has a name, not a drag */}
      <div className="flex flex-wrap gap-1 pb-1">
        {PRESETS.map((preset) => {
          const selected =
            preset.from === active.from && preset.to === active.to;
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() =>
                onRangeCommit({ from: preset.from, to: preset.to })
              }
              aria-pressed={selected}
              className={`border border-dashed border-theme-color px-2 text-xs hover:opacity-50 ${
                selected ? "font-bold" : "opacity-70"
              }`}
              style={
                selected
                  ? {
                      color: "var(--accent-color)",
                      borderColor: "var(--accent-color)",
                    }
                  : undefined
              }
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {/* The histogram, doubling as the range control */}
      <div className="relative">
        <svg
          ref={svgRef}
          width="100%"
          height={CHART_HEIGHT + AXIS_BAND}
          viewBox={`0 0 ${width} ${CHART_HEIGHT + AXIS_BAND}`}
          role="img"
          aria-label={`Artifact counts by time period. Current selection ${
            isAll ? "all periods" : `${formatYear(active.from)} to ${formatYear(active.to)}`
          }.`}
          style={{
            touchAction: "none",
            cursor: draft ? "grabbing" : "crosshair",
            // Refetch holds the previous render rather than flashing a skeleton
            opacity: loading ? 0.6 : 1,
            transition: "opacity 120ms linear",
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={(e) => {
            setHovered(null);
            endDrag(e);
          }}
        >
          {/* Selection band, so the range is legible without relying on bar color */}
          {!isAll && (
            <rect
              x={Math.min(fromIndex, toIndex) * columnWidth}
              y={0}
              width={(Math.abs(toIndex - fromIndex) + 1) * columnWidth}
              height={CHART_HEIGHT}
              fill="var(--accent-color)"
              opacity={0.08}
            />
          )}

          {ranges.map((bucket, i) => {
            const count = counts[i];
            const inRange =
              i >= Math.min(fromIndex, toIndex) &&
              i <= Math.max(fromIndex, toIndex);
            const scaled = (count / maxCount) * (CHART_HEIGHT - 4);
            const height =
              count === 0 ? 0 : Math.max(MIN_BAR_HEIGHT, scaled);
            const x = i * columnWidth + BAR_GAP / 2;
            const barWidth = Math.max(1, columnWidth - BAR_GAP);
            return (
              <g key={bucket.start}>
                {height > 0 && (
                  <path
                    d={barPath(x, CHART_HEIGHT - height, barWidth, height)}
                    fill="var(--accent-color)"
                    opacity={inRange ? 1 : 0.22}
                  />
                )}
                {/* Full-height hit target: a 2px bar is not a click target */}
                <rect
                  x={i * columnWidth}
                  y={0}
                  width={columnWidth}
                  height={CHART_HEIGHT}
                  fill="transparent"
                >
                  <title>{`${formatBucketLabel(bucket)}: ${count.toLocaleString()} artifacts`}</title>
                </rect>
              </g>
            );
          })}

          {/* Baseline: a solid hairline, one shade off the surface */}
          <line
            x1={0}
            y1={CHART_HEIGHT}
            x2={width}
            y2={CHART_HEIGHT}
            stroke="var(--border-color)"
            strokeWidth={1}
          />

          {/* Handles — focusable, so the range is reachable without a pointer */}
          {!isAll &&
            (
              [
                ["from", Math.min(fromIndex, toIndex) * columnWidth],
                ["to", (Math.max(fromIndex, toIndex) + 1) * columnWidth],
              ] as const
            ).map(([which, x]) => (
              <g
                key={which}
                tabIndex={0}
                role="slider"
                aria-label={which === "from" ? "Earliest year" : "Latest year"}
                aria-valuemin={TIMELINE_MIN_YEAR}
                aria-valuemax={TIMELINE_MAX_YEAR}
                aria-valuenow={which === "from" ? active.from : active.to}
                aria-valuetext={formatYear(
                  which === "from" ? active.from : active.to,
                )}
                onKeyDown={handleKeyDown(which)}
                style={{ cursor: "ew-resize" }}
              >
                <line
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={CHART_HEIGHT}
                  stroke="var(--accent-color)"
                  strokeWidth={2}
                />
                <rect
                  x={x - 4}
                  y={CHART_HEIGHT / 2 - 9}
                  width={8}
                  height={18}
                  rx={2}
                  fill="var(--accent-color)"
                />
                {/* Widened grab area, independent of the drawn handle */}
                <rect
                  x={x - 12}
                  y={0}
                  width={24}
                  height={CHART_HEIGHT}
                  fill="transparent"
                />
              </g>
            ))}

          {/* Axis ticks. Every bucket start would collide, so only every Nth
              is drawn, plus the axis end so the extent is always labelled. */}
          {ranges.map((bucket, i) =>
            i % tickStep === 0 && i < ranges.length - 1 ? (
              <text
                key={`tick-${bucket.start}`}
                x={i * columnWidth + columnWidth / 2}
                y={CHART_HEIGHT + 13}
                textAnchor="middle"
                fontSize={9}
                fill="var(--txt-color-annotation)"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {formatYearShort(bucket.start)}
              </text>
            ) : null,
          )}
          <text
            x={width}
            y={CHART_HEIGHT + 13}
            textAnchor="end"
            fontSize={9}
            fill="var(--txt-color-annotation)"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatYearShort(ranges[ranges.length - 1].end + 1)}
          </text>
        </svg>

        {/* Hover tooltip — enhances the title/table, never the only way to read */}
        {hoveredBucket && hovered !== null && (
          <div
            className="pointer-events-none absolute border border-theme-color bg-theme-color px-2 py-1 text-xs"
            style={{
              left: Math.min(
                Math.max(hovered * columnWidth + columnWidth / 2 - 60, 0),
                Math.max(width - 120, 0),
              ),
              top: -4,
              width: 120,
              textAlign: "center",
              backgroundColor: "var(--bg-color)",
            }}
          >
            <span className="font-bold">{formatBucketLabel(hoveredBucket)}</span>
            <br />
            {counts[hovered].toLocaleString()}
          </div>
        )}
      </div>

      {/* Exact years, behind the presets: the custom range in the footer */}
      <div className="flex flex-wrap items-center gap-2 pt-2 text-xs">
        <label className="flex items-center gap-1">
          <span className="opacity-60">FROM</span>
          <input
            type="number"
            defaultValue={range.from}
            key={`from-${range.from}`}
            min={TIMELINE_MIN_YEAR}
            max={TIMELINE_MAX_YEAR}
            onBlur={(e) => commitYear("from", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitYear("from", e.currentTarget.value);
            }}
            className="w-20 border p-1"
            aria-label="Earliest year, negative for BCE"
          />
        </label>
        <label className="flex items-center gap-1">
          <span className="opacity-60">TO</span>
          <input
            type="number"
            defaultValue={range.to}
            key={`to-${range.to}`}
            min={TIMELINE_MIN_YEAR}
            max={TIMELINE_MAX_YEAR}
            onBlur={(e) => commitYear("to", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitYear("to", e.currentTarget.value);
            }}
            className="w-20 border p-1"
            aria-label="Latest year, negative for BCE"
          />
        </label>
        <span className="opacity-40">negative = BCE</span>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="ml-auto border border-dashed border-theme-color px-2 hover:opacity-50"
          aria-expanded={showTable}
        >
          {showTable ? "HIDE COUNTS" : "COUNTS"}
        </button>
      </div>

      {/* Table view: every value the chart encodes, in text */}
      {showTable && (
        <div className="mt-1 max-h-40 overflow-y-auto border p-2">
          <table className="w-full text-xs">
            <caption className="sr-only">
              Artifact counts by time period
            </caption>
            <thead>
              <tr className="opacity-60">
                <th scope="col" className="text-left font-normal">
                  PERIOD
                </th>
                <th scope="col" className="text-right font-normal">
                  ARTIFACTS
                </th>
              </tr>
            </thead>
            <tbody>
              {ranges.map((bucket, i) => (
                <tr key={bucket.start}>
                  <td>{formatBucketLabel(bucket)}</td>
                  <td
                    className="text-right"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {counts[i].toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pt-1 opacity-40">
            An object dated across several periods is counted in each one it
            spans, so the column totals more than the collection.
          </p>
        </div>
      )}
    </div>
  );
};
